import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Zap, ArrowRight } from 'lucide-react';
import { Language, View } from '../../types';
import { swissSpring, swissStaggerContainer, swissCardVariants } from '../../lib/animations';
import { TiltCardSimple } from '../ui/TiltCard';

interface HowIHelpTeaserProps {
  lang: Language;
  setView: (v: View) => void;
}

const HowIHelpTeaser: React.FC<HowIHelpTeaserProps> = ({ lang, setView }) => {

  const content = {
    label: {
      en: "PROCESS",
      fa: "فرایند"
    },
    title: {
      en: "Process",
      fa: "فرایند"
    },
    intro: {
      en: "Coaching is a structured partnership for redesigning your inner system.",
      fa: "کوچینگ یک شراکت ساختاریافته برای بازطراحی سیستم درونی تو است."
    },
    link: {
      en: "Learn More About My Approach",
      fa: "بیشتر درباره رویکرد من"
    },
    benefits: [
      {
        icon: Target,
        title: { en: "Radical Clarity", fa: "وضوح رادیکال" },
        description: {
          en: "Understand what you truly want, not what you think you should want.",
          fa: "بفهم واقعا چی می‌خوای، نه اون چیزی که فکر می‌کنی باید بخوای."
        }
      },
      {
        icon: Eye,
        title: { en: "Self-Awareness", fa: "خودآگاهی" },
        description: {
          en: "See the patterns driving your behavior and choices.",
          fa: "الگوهایی که رفتار و انتخاب‌هات رو شکل می‌دن رو ببین."
        }
      },
      {
        icon: Zap,
        title: { en: "Action Without Force", fa: "اقدام بدون زور" },
        description: {
          en: "Move from 'I have to' to 'I want to'.",
          fa: "از «باید» به «می‌خوام» برس."
        }
      }
    ]
  };

  return (
    <section className="py-28 md:py-36 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header - Asymmetric Layout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={swissSpring}
          className="grid md:grid-cols-12 gap-8 md:gap-12 mb-20"
        >
          {/* Title Column */}
          <div className="md:col-span-5">
            <h2 className="swiss-headline text-4xl md:text-5xl text-brandDark text-balance">
              {content.title[lang]}
            </h2>
          </div>

          {/* Intro Column */}
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="swiss-body text-lg md:text-xl text-gray-500 text-pretty">
              {content.intro[lang]}
            </p>
          </div>
        </motion.div>

        {/* Benefits Cards - Swiss Grid with 3D Tilt */}
        <motion.div
          variants={swissStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-3 gap-px bg-gray-200 p-px rounded-xl overflow-hidden mb-12"
        >
          {content.benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            const number = String(index + 1).padStart(2, '0');
            return (
              <motion.div
                key={index}
                variants={swissCardVariants}
              >
                <TiltCardSimple
                  className="group relative p-10 md:p-12 bg-white overflow-hidden cursor-pointer h-full"
                  tiltAmount={5}
                  scaleOnHover={1.02}
                >
                  {/* Large Swiss Number */}
                  <span className="absolute top-8 right-8 swiss-number text-6xl md:text-7xl text-gray-100 transition-colors group-hover:text-primary/15">
                    {number}
                  </span>

                  {/* Icon */}
                  <div className="relative mb-8">
                    <Icon className="w-8 h-8 text-primary" aria-hidden="true" />
                  </div>

                  {/* Title */}
                  <h3 className="relative text-lg md:text-xl font-bold text-brandDark mb-3">
                    {benefit.title[lang]}
                  </h3>

                  {/* Description */}
                  <p className="relative text-base text-gray-500 leading-relaxed">
                    {benefit.description[lang]}
                  </p>

                  {/* Hover bottom line */}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform motion-reduce:transition-none origin-left" />
                </TiltCardSimple>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={swissSpring}
          className="text-center"
        >
          <a
            href="#how-i-help"
            onClick={(e) => { e.preventDefault(); setView('how-i-help'); }}
            className="inline-flex items-center gap-2 text-primary font-medium text-base hover:text-primary/80 transition-colors group focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none rounded"
          >
            {content.link[lang]}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform motion-reduce:transition-none" aria-hidden="true" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowIHelpTeaser;
